<template>
  <div class="shops-slider" :class="{'shops-slider--overflow': isVisibleOverflow}">
    <header class="shops-slider__header">
      <div class="shops-slider__title-wrap">
        <h3 class="shops-slider__title">{{ title }}</h3>
      </div>
      <div class="shops-slider__navigation">
        <div class="swiper-button-prev swiper-button-prev--shops" slot="button-prev"></div>
        <div class="swiper-button-next swiper-button-next--shops" slot="button-next"></div>
      </div>
    </header>
    <ul class="shops-slider__filters-list page-container">
      <li class="shops-slider__filters-item" v-for="(filter, idx) in selectedFilters" :key="idx">
        {{ filter.label }}
        <button @click="handleFilters(filter)" class="shops-slider__delete-btn">
          <svg width="10"
               height="10"
               viewBox="0 0 10 10"
               fill="none"
               xmlns="http://www.w3.org/2000/svg">
            <rect width="1.66377"
                  height="12.4782"
                  rx="0.831883"
                  transform="matrix(0.707102 -0.707111 0.707102 0.707111 0 1.17651)"
                  fill="#909090"/>
            <rect width="1.66377"
                  height="12.4782"
                  rx="0.831883"
                  transform="matrix(0.707102 0.707111 -0.707102 0.707111 8.82349 0)"
                  fill="#909090"/>
          </svg>
        </button>
      </li>
    </ul>
    <swiper class="swiper" :options="swiperOption">
      <swiper-slide class="swiper-item" v-for="(item, i) in shops.slice(0, 40)" :key="i">
        <ShopCard @open="handleShopCard"
                  :shop-data="item"
                  is-adaptive
                  is-slider />
      </swiper-slide>
    </swiper>
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import { Swiper, SwiperSlide } from 'vue-awesome-swiper'
import { isTouch } from '../../mixins/isTouchDevice'
import { wordFormatter } from '../../mixins/wordFormatter'
import ShopCard from './ShopCard'

export default {
  name: 'ShopsSlider',
  mixins: [isTouch, wordFormatter],
  components: {
    Swiper,
    SwiperSlide,
    ShopCard
  },
  computed: {
    ...mapState({
      services: state => state.shops.services,
      filteredFlags: state => state.shops.filteredFlags
    }),
    title () {
      return this.wordFormatter(this.shops.length, [this.$t('shops_slider_fined_1'), this.$t('shops_slider_fined_2'), this.$t('shops_slider_fined_2')]) + ' ' + this.shops.length + ' ' + this.wordFormatter(this.shops.length, [this.$t('shops_slider_shop_1'), this.$t('shops_slider_shop_2'), this.$t('shops_slider_shop_3')])
    },
    selectedFilters () {
      const result = []

      this.services.forEach(service => {
        if (this.filteredFlags.services[service.key]) {
          result.push({ ...service, type: 'service' })
        }
      })

      if (this.filteredFlags['opened']) {
        result.push({ key: 'opened', label: this.$t('shops_filters_opened') })
      }

      if (this.filteredFlags['near_subway']) {
        result.push({ key: 'near_subway', label: this.$t('shops_filters_near_subway') })
      }

      return result
    }
  },
  props: {
    shops: {
      type: Array,
      default: () => []
    }
  },
  data () {
    return {
      isVisibleOverflow: false,
      swiperOption: {
        watchOverflow: true,
        watchSlidesVisibility: true,
        spaceBetween: 20,
        slidesPerView: 1.5,
        navigation: {
          nextEl: '.swiper-button-next--shops',
          prevEl: '.swiper-button-prev--shops'
        },
        simulateTouch: !isTouch,
        touchStartPreventDefault: false,
        preloadImages: false,
        lazy: true,
        breakpoints: {
          319: {
            slidesPerView: 1.2
          },
          550: {
            slidesPerView: 2.2
          },
          769: {
            slidesPerView: 3
          },
          1100: {
            slidesPerView: 4
          }
        }
      },
      openedSlidesCount: 0
    }
  },
  methods: {
    ...mapActions(['SET_FILTERED_FLAGS_ACTION']),
    handleShopCard (e) {
      e ? this.openedSlidesCount++ : this.openedSlidesCount--
      this.isVisibleOverflow = this.openedSlidesCount
    },
    handleFilters (item) {
      this.SET_FILTERED_FLAGS_ACTION({ key: item.key, value: false, type: item.type })
    }
  }
}
</script>

<style scoped lang="sass">
@import "~swiper/css/swiper.min.css"

@import "../../styles/swiper/swiper"

.shops-slider ::v-deep
  .swiper-container
    padding-bottom: 3px

  .swiper-wrapper
    align-items: unset
    padding: 0 15px

    +mmin($tablet)
      padding: 0

  .swiper-item
    height: auto

.shops-slider--overflow .swiper-container
  z-index: 3
  padding-bottom: 403px
  margin-bottom: -400px

.shops-slider__header
  padding: 0 15px

.swiper-container
  +mmin($tablet)
    padding: 0 15px

.shops-slider--hovered .swiper-container
  +mmin($tablet)
    z-index: 3
    padding-top: 5px
    padding-bottom: 400px
    margin-top: -5px
    margin-bottom: -400px

.shops-slider__header
  display: flex
  flex-flow: row nowrap
  justify-content: space-between
  align-items: center
  margin-bottom: 15px

  +mmin($tablet--hor)
    margin-bottom: 30px

.shops-slider__title-wrap
  display: flex
  flex-flow: row wrap
  align-items: baseline
  justify-content: space-between
  width: 100%

  +mmin($tablet)
    width: auto
    justify-content: flex-start

.shops-slider__title
  font-size: 18px
  font-weight: 500
  padding: 0
  margin: 0

  +mmin($tablet)
    font-size: 24px

.shops-slider__more
  color: $main-color
  font-size: 14px
  font-weight: 400 //Rubik 300

  +mmin($tablet)
    font-size: 16px
    margin-left: 20px

  &:hover,
  &:focus,
  &:active
    color: $main-color--hover

.shops-slider__navigation
  display: none
  position: relative
  width: 70px

  +mmin($tablet)
    display: block

.shops-slider__filters-list
  +list-unstyled
  display: flex
  flex-flow: row wrap
  padding: 0 15px
  margin: 0 auto 10px

  +mmin($tablet--hor)
    margin: 0 auto 30px

.shops-slider__filters-item
  display: flex
  flex-flow: row nowrap
  align-items: baseline
  width: max-content
  padding: 2px 10px 2px 15px
  color: $placeholder-color
  border-radius: 3px
  border: 1px solid #e7e7e7
  font-size: 12px
  margin: 0 5px 5px 0

  +mmin($tablet--hor)
    font-size: 16px
    margin: 0 20px 10px 0

.shops-slider__delete-btn
  cursor: pointer
  display: block
  opacity: .6
  border: 0
  padding: 5px
  background-color: transparent
  margin-left: 10px
  line-height: 1
  transform: scale(.8)

  &:hover
    opacity: 1

  +mmin($tablet--hor)
    transform: none

</style>
